import { LinearProgress } from "@mui/material";
import { useEffect } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useLocation, Location, useNavigate } from "react-router-dom";
import { useSnackbar } from "@myplant-io/snackbar";
import { RouteNames } from "../RouteNames";
import { useTeams } from "../state/Teams";
import { teamsAuthPopup } from "../utils/teamsAuth";
import { useAuth } from "@myplant-io/hooks";
import { API_BASE_URL } from "../constants";

const messages = defineMessages({
  loginError: {
    id: "login.teams.error",
    defaultMessage: "Login failed. Please try again.",
  },
});

const getRedirectLocation = (location: Location) =>
  location.state && location.state.from
    ? `${location.state.from.pathname}${location.state.from.search}`
    : RouteNames.ROOT;

export function LoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isInTeams, isInitialized } = useTeams();
  const { refetch } = useAuth(API_BASE_URL);
  const { showErrorMessage } = useSnackbar();
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (!isInitialized) return;

    if (isInTeams) {
      // In Teams: open a popup for the OAuth flow.
      // The popup will navigate through the OAuth server and land on
      // /teams-auth-complete which calls notifySuccess() to close itself.
      teamsAuthPopup(
        `/login?redirect_uri=${encodeURIComponent(
          RouteNames.TEAMS_AUTH_COMPLETE
        )}`
      )
        .then(() => {
          // Popup closed successfully — session cookie is now set.
          // Re-fetch the user to pick up the new session.
          refetch();
          const target = getRedirectLocation(location);
          navigate(target, { replace: true });
        })
        .catch((reason: unknown) => {
          // "CancelledByUser" means the user deliberately closed the popup —
          // navigate silently without showing an error.
          if (reason !== "CancelledByUser") {
            showErrorMessage({
              message: formatMessage(messages.loginError),
              disableClickAway: true,
            });
          }
          navigate(RouteNames.ROOT, { replace: true });
        });
    } else {
      // Regular browser: full-page redirect to the backend OAuth flow.
      document.location.href = `/login?redirect_uri=${encodeURIComponent(
        getRedirectLocation(location)
      )}`;
    }
  }, [
    formatMessage,
    isInTeams,
    isInitialized,
    location,
    navigate,
    refetch,
    showErrorMessage,
  ]);

  return <LinearProgress />;
}
